import React, { useEffect, useState } from 'react';
import { localToday, dateStr } from '../utils/dates';
import { auth, db } from '../firebase';
import { resolveMember } from '../utils/resolveMember';
import Layout from '../components/Layout';
import AdultNextSteps from '../components/AdultNextSteps';
import { collection, getDocs, getDoc, doc, query, where, orderBy, limit, Timestamp, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Box, Grid, CircularProgress, Tabs, Tab, useMediaQuery, IconButton, Badge, Menu, MenuItem, Button, Divider, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { refundCheckInsForClasses } from '../utils/classRefunds';
import { isTestAccount } from '../utils/testAccounts';
import { membershipStatus } from '../utils/membership';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Barlow:wght@300;400;600&display=swap');
  .tbv-dash { background: #0D1E35; min-height: 100%; padding: 32px; font-family: 'Barlow', sans-serif; }
  @media (max-width: 768px) {
    .tbv-dash { padding: 16px; }
    .tbv-panel { padding: 16px !important; }
  }
  .tbv-dash-welcome {
    font-family: 'Bebas Neue', sans-serif; font-size: clamp(1.8rem, 3vw, 2.4rem);
    letter-spacing: 3px; color: white; line-height: 1;
  }
  .tbv-dash-sub {
    font-size: 0.78rem; font-weight: 300; color: rgba(255,255,255,0.35);
    letter-spacing: 1.5px; text-transform: uppercase; margin-top: 6px;
  }
  .tbv-dash-divider {
    width: 50px; height: 2px;
    background: linear-gradient(90deg, #C9A84C, transparent);
    margin: 14px 0 32px;
  }
  .tbv-panel {
    background: rgba(255,255,255,0.06); border: 1px solid rgba(201,168,76,0.12);
    border-radius: 14px; padding: 24px; height: 100%;
    /* height: 100% plus padding overflowed the grid cell by 50px — invisible
       until Active Members sat beneath and was overlapped by it. */
    box-sizing: border-box;
  }
  .tbv-news-panel {
    background: rgba(201,168,76,0.07); border: 1px solid rgba(201,168,76,0.28);
    border-radius: 14px; padding: 18px 20px; margin-bottom: 24px;
    display: flex; align-items: center; gap: 20px; flex-wrap: wrap;
  }
  .tbv-news-copy { flex: 1 1 240px; min-width: 0; }
  .tbv-news-title {
    font-family: 'Bebas Neue', sans-serif; font-size: 1.1rem; letter-spacing: 2px;
    color: rgba(201,168,76,0.9);
  }
  .tbv-news-sub { font-size: 0.76rem; font-weight: 300; color: rgba(255,255,255,0.45); line-height: 1.5; margin-top: 3px; }
  .tbv-news-form { display: flex; gap: 8px; flex-wrap: wrap; flex: 2 1 380px; }
  .tbv-news-input {
    flex: 1 1 150px; min-width: 0; padding: 9px 12px; border-radius: 8px;
    background: rgba(255,255,255,0.06); border: 1px solid rgba(201,168,76,0.3);
    color: white; font-family: 'Barlow', sans-serif; font-size: 0.82rem;
  }
  .tbv-news-input::placeholder { color: rgba(255,255,255,0.35); }
  .tbv-news-input:focus { outline: none; border-color: #C9A84C; }
  .tbv-news-btn {
    flex: 0 0 auto; padding: 9px 20px; border-radius: 8px; border: none; cursor: pointer;
    background: #C9A84C; color: #0D1E35; font-family: 'Barlow', sans-serif;
    font-weight: 600; font-size: 0.8rem; letter-spacing: 1px; text-transform: uppercase;
  }
  .tbv-news-btn:hover:not(:disabled) { background: #e0be6a; }
  .tbv-news-btn:disabled { opacity: 0.55; cursor: default; }
  .tbv-news-skip {
    flex: 0 0 auto; padding: 9px 10px; border: none; background: none; cursor: pointer;
    color: rgba(255,255,255,0.35); font-family: 'Barlow', sans-serif; font-size: 0.76rem;
  }
  .tbv-news-skip:hover { color: rgba(255,255,255,0.6); }
  .tbv-news-msg { flex: 1 1 100%; font-size: 0.78rem; line-height: 1.5; }
  .tbv-news-ok { color: #7FCB9B; }
  .tbv-news-err { color: #E28E82; }
  .tbv-panel-title {
    font-family: 'Bebas Neue', sans-serif; font-size: 1.1rem; letter-spacing: 2px;
    color: rgba(201,168,76,0.8); margin-bottom: 16px; padding-bottom: 10px;
    border-bottom: 1px solid rgba(201,168,76,0.1);
  }
  .tbv-activity-item {
    padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,0.06);
    font-size: 0.82rem; color: rgba(255,255,255,0.7); line-height: 1.5;
  }
  .tbv-activity-item:last-child { border-bottom: none; padding-bottom: 0; }
  .tbv-activity-item strong { color: #C9A84C; font-weight: 600; }
  /* A resting underline, not just a hover one. Every bold word in this feed is
     gold — "3 sessions" and "Gold member" look exactly like a person's name —
     so with a transparent resting border there was nothing to tell a reader
     which of them go anywhere. The links worked; nobody could see them. */
  .tbv-activity-link { text-decoration: none; border-bottom: 1px solid rgba(201,168,76,0.4); }
  .tbv-activity-link:hover { border-bottom-color: rgba(201,168,76,0.95); }
  .tbv-activity-link:focus-visible { outline: 2px solid #C9A84C; outline-offset: 2px; border-radius: 2px; }
  .tbv-activity-date {
    font-size: 0.68rem; color: rgba(201,168,76,0.5); letter-spacing: 1px;
    text-transform: uppercase; margin-top: 3px;
  }
  .tbv-event-item {
    padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .tbv-event-item:last-child { border-bottom: none; padding-bottom: 0; }
  .tbv-event-name { font-size: 0.85rem; font-weight: 600; color: white; display: flex; align-items: center; gap: 8px; }
  .tbv-event-meta { font-size: 0.72rem; color: rgba(255,255,255,0.35); margin-top: 3px; }
  .tbv-event-badge {
    display: inline-block; font-size: 0.58rem; font-weight: 600; letter-spacing: 1px;
    text-transform: uppercase; padding: 2px 7px; border-radius: 4px; flex-shrink: 0;
    border: 1px solid rgba(201,168,76,0.3); color: rgba(201,168,76,0.7); white-space: nowrap;
  }
  .tbv-event-badge-birthday {
    display: inline-block; font-size: 0.58rem; font-weight: 600; letter-spacing: 1px;
    text-transform: uppercase; padding: 2px 7px; border-radius: 4px; flex-shrink: 0;
    border: 1px solid rgba(232,121,97,0.4); color: rgba(232,121,97,0.9);
  }
  .tbv-event-item-tournament {
    background: rgba(201,168,76,0.10);
    border: 1px solid rgba(201,168,76,0.35);
    border-radius: 8px;
    padding: 10px 12px !important;
    margin: 6px 0;
  }
  .tbv-event-item-tournament .tbv-event-name { color: #C9A84C; }
  .tbv-event-badge-tournament {
    display: inline-block; font-size: 0.58rem; font-weight: 700; letter-spacing: 1px;
    text-transform: uppercase; padding: 2px 7px; border-radius: 4px; flex-shrink: 0;
    background: #C9A84C; color: #0D1E35; white-space: nowrap;
  }
  .tbv-event-badge-clinic {
    display: inline-block; font-size: 0.58rem; font-weight: 700; letter-spacing: 1px;
    text-transform: uppercase; padding: 2px 7px; border-radius: 4px; flex-shrink: 0;
    background: #8E44AD; color: white; white-space: nowrap;
  }
  .tbv-event-badge-camp {
    display: inline-block; font-size: 0.58rem; font-weight: 700; letter-spacing: 1px;
    text-transform: uppercase; padding: 2px 7px; border-radius: 4px; flex-shrink: 0;
    background: #1ABC9C; color: #0D1E35; white-space: nowrap;
  }
  .tbv-event-badge-league {
    display: inline-block; font-size: 0.58rem; font-weight: 700; letter-spacing: 1px;
    text-transform: uppercase; padding: 2px 7px; border-radius: 4px; flex-shrink: 0;
    background: #3498DB; color: white; white-space: nowrap;
  }
  .tbv-empty { font-size: 0.78rem; font-weight: 300; color: rgba(255,255,255,0.25); line-height: 1.6; }
  .tbv-spinner { display: flex; justify-content: center; padding: 24px 0; }
`;

function pronoun(gender, type) {
  const g = (gender || '').toLowerCase();
  if (g === 'male') return type === 'subject' ? 'He' : type === 'possessive' ? 'his' : 'him';
  if (g === 'female') return type === 'subject' ? 'She' : type === 'possessive' ? 'her' : 'her';
  return type === 'subject' ? 'They' : type === 'possessive' ? 'their' : 'them';
}

function fmtDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateStr(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[+m - 1]} ${+d}, ${y}`;
}

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function calcGradYear(grade) {
  const g = parseInt(grade, 10);
  if (isNaN(g)) return null;
  return new Date().getFullYear() + (12 - g);
}

function formatSessionLabel(t) {
  const map = {
    'junior-practice':   'Junior Practice',
    'junior-private':    'Junior Private',
    'junior-drop-in':    'Junior Drop-in',
    'junior-evaluation': 'Junior Evaluation',
    'adult-practice':    'Adult Practice',
    'adult-drop-in':     'Adult Drop-in',
    'league':            'League',
    'block-time':        'Block Time',
  };
  return map[t] || (t ? t.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Class');
}

function Dashboard() {
  const user = auth.currentUser;
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileTab, setMobileTab] = useState(0);
  const [firstName, setFirstName] = useState(user?.displayName?.split(' ')[0] || 'Admin');

  const [activity, setActivity] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [partialRegs, setPartialRegs] = useState([]);

  // Stale class bell (admins + coaches): past practice classes with no check-ins
  const [isStaffViewer, setIsStaffViewer] = useState(false);
  // null while loading; then everyone currently a member, A–Z.
  const [activeMembers, setActiveMembers] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [staleClasses, setStaleClasses] = useState([]);
  const [shirtTasks, setShirtTasks] = useState([]);
  const [pendingWork, setPendingWork] = useState([]);
  const [unpaidCoaches, setUnpaidCoaches] = useState([]);
  const [bellAnchor, setBellAnchor] = useState(null);
  const [bellOpen, setBellOpen] = useState({ stale: false, shirts: false, work: false, coaches: false });
  const [cancelDialog, setCancelDialog] = useState(null); // class object or null
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSaving, setCancelSaving] = useState(false);

  // Newsletter prompt. null = don't show; an object = show it, prefilled from
  // the account. Whether it has been answered lives on the user doc rather than
  // localStorage, so signing up on the laptop stops the nag on the phone.
  const [nlForm, setNlForm] = useState(null);   // { name, email, website }
  const [showAdultSurvey, setShowAdultSurvey] = useState(false);
  const [nlSending, setNlSending] = useState(false);
  const [nlMsg, setNlMsg] = useState(null);     // { ok, text }

  // Every active member, for the list under Recent Activity and Upcoming
  // Events. Good standing (active, trialing — trialing is a prepaid annual)
  // plus court renters, whose rental tier is simply 'active'. Not frozen, past
  // due or canceled; not day passes, lapsed one-time passes, merged-login stubs,
  // archived or switched-off accounts, or test accounts. Names and plan only — every signed-in account sees it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'users'),
          where('membership.status', 'in', ['active', 'trialing'])));
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          // Archived and switched-off accounts are hidden everywhere else
          // (People's tabs, its Status column), so here too — an archived
          // account can still hold a live membership.
          .filter(u => !u.mergedIntoUid && !isTestAccount(u) && u.membership?.tierId
            && u.isArchived !== true && u.active !== false
            && u.membership.isDayPass !== true && membershipStatus(u) !== 'expired')
          .map(u => ({
            id: u.id,
            name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || '—',
            plan: (u.membership.tierName || '').replace(/ Membership/, ''),
            // Gold tiers (Annual and Monthly) are shown in the club gold.
            gold: /gold/i.test(u.membership.tierName || ''),
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        if (!cancelled) setActiveMembers(list);
      } catch (e) {
        console.error('Active members load error:', e);
        if (!cancelled) setActiveMembers([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    loadActivity();
    loadEvents();
    if (user) {
      // Canonical, not the auth uid: a merged login's own doc carries only the
      // pointer, so the greeting came out blank for those accounts.
      resolveMember(db, user)
        .then(({ data }) => {
          if (data) {
            if (data.firstName) setFirstName(data.firstName);
            const roles = data.roles || [];
            const isStaff = roles.some(r => ['admin','coach','assistant_coach'].includes(r));
            setIsStaffViewer(isStaff);
            setIsAdmin(roles.includes('admin'));
            if (isStaff) { loadStaleClasses(); loadShirtTasks(); loadPendingWork(); loadUnpaidCoaches(); }
            // The adult survey, for a newish adult account that has neither
            // answered it nor said "Not now". Whichever tile they came from
            // (Adult Training or Memberships), it lands here eventually, where
            // the post-registration welcome page can be skipped by checkout.
            const created = Date.parse(user.metadata?.creationTime || '') || 0;
            const isAdultAccount = roles.includes('adult_athlete') || roles.includes('member');
            if (isAdultAccount && !isStaff && Date.now() - created < 120 * 864e5) {
              getDoc(doc(db, 'adultInterests', user.uid)).then(snap => {
                const d = snap.exists() ? snap.data() : {};
                if (!(d.training || d.league || d.member || d.info) && !d.dismissedAt) setShowAdultSurvey(true);
              }).catch(() => {});
            }
            // Asked once. Either answer — signed up or dismissed — retires it.
            if (!data.newsletterSignupAt && !data.newsletterPromptDismissedAt) {
              setNlForm({
                name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || user.displayName || '',
                email: data.email || user.email || '',
                website: '',
              });
            }
          }
        })
        .catch(() => {});
      // Check for half-paid tournament registrations
      getDocs(query(
        collection(db, 'registrations'),
        where('email', '==', (user.email || '').toLowerCase()),
        where('status', '==', 'active'),
        where('payment_status', '==', 'half_paid')
      )).then(snap => setPartialRegs(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
        .catch(() => {});
    }
  }, []);

  // ── Newsletter prompt ──────────────────────────────────────────────────────
  // Same public function the home page calls; signed in only changes what the
  // fields start out as. The address stays editable — a parent signing up on a
  // different inbox than the one the account uses is a normal thing to want.
  const setNl = (k) => (e) => setNlForm(f => ({ ...f, [k]: e.target.value }));

  async function markNewsletterAnswered(field) {
    if (!user) return;
    try {
      await updateDoc(doc(db, 'users', user.uid), { [field]: serverTimestamp() });
    } catch (e) {
      // Worst case the prompt comes back next visit — not worth an error.
      console.warn('newsletter prompt stamp failed:', e);
    }
  }

  async function handleSubscribe(e) {
    e.preventDefault();
    const name = (nlForm?.name || '').trim();
    const email = (nlForm?.email || '').trim();
    if (!name) { setNlMsg({ ok: false, text: 'Please enter your name.' }); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setNlMsg({ ok: false, text: 'Please enter a valid email address.' }); return;
    }
    setNlSending(true); setNlMsg(null);
    try {
      const fn = httpsCallable(getFunctions(), 'subscribeNewsletter');
      const r = await fn({ name, email, website: nlForm.website });
      // An address that unsubscribed before stays suppressed — the form can't
      // undo that, so say so rather than promise email that will never arrive.
      if (r.data?.optedOut) {
        setNlMsg({ ok: false, text: 'That address previously unsubscribed from club email, so we’ve left it off. Email info@torchvolleyball.com to turn it back on.' });
      } else {
        setNlMsg({ ok: true, text: r.data?.alreadySubscribed ? 'You’re already on the list.' : 'You’re on the list. Watch your inbox for club news.' });
        await markNewsletterAnswered('newsletterSignupAt');
      }
    } catch (err) {
      console.warn('newsletter signup failed:', err);
      setNlMsg({ ok: false, text: err?.message || 'Something went wrong. Please try again.' });
    }
    setNlSending(false);
  }

  async function dismissNewsletterPrompt() {
    setNlForm(null);
    setNlMsg(null);
    await markNewsletterAnswered('newsletterPromptDismissedAt');
  }

  // Load past practice classes with no check-ins, not canceled, not acknowledged.
  // Bell shows count; clicking opens dropdown with per-class Cancel/Acknowledge buttons.
  async function loadStaleClasses() {
    try {
      const today = localToday();
      // Fetch candidate classes: past, practice-type, not canceled, not acknowledged
      const snap = await getDocs(query(
        collection(db, 'classes'),
        where('date', '<', today),
        orderBy('date', 'desc'),
        limit(100)
      ));
      const candidates = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(c => ['junior-practice','adult-practice'].includes(c.sessionType))
        .filter(c => c.status !== 'canceled')
        .filter(c => c.noShowAcknowledged !== true);

      // Check attendance for each. Could optimize with batched/grouped queries later.
      const stale = [];
      for (const c of candidates) {
        const attSnap = await getDocs(query(
          collection(db, 'attendance'),
          where('classId', '==', c.id),
          limit(1)
        ));
        if (attSnap.empty) stale.push(c);
        if (stale.length >= 25) break; // cap to avoid runaway loops
      }
      setStaleClasses(stale);
    } catch (e) {
      console.error('Failed to load stale classes:', e);
    }
  }

  async function markClassCanceled(classId, reason) {
    if (!reason) return;
    setCancelSaving(true);
    try {
      await updateDoc(doc(db, 'classes', classId), {
        status: 'canceled',
        cancelReason: reason,
        canceledAt: new Date(),
      });
      // Anyone already checked in paid for a class that didn't happen.
      await refundCheckInsForClasses([classId]);
      setStaleClasses(prev => prev.filter(c => c.id !== classId));
      setCancelDialog(null);
      setCancelReason('');
    } catch (e) {
      console.error('Cancel failed:', e);
      alert('Failed to cancel: ' + e.message);
    }
    setCancelSaving(false);
  }

  async function loadPendingWork() {
    try {
      const snap = await getDocs(query(collection(db, 'workEntries'), where('paid', '==', false)));
      let items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Resolve names for entries created before workerName was denormalized
      const missing = [...new Set(items.filter(w => !w.workerName).map(w => w.workerId))];
      if (missing.length) {
        const nameMap = {};
        await Promise.all(missing.map(async id => {
          try {
            const u = await getDoc(doc(db, 'users', id));
            if (u.exists()) nameMap[id] = `${u.data().firstName || ''} ${u.data().lastName || ''}`.trim();
          } catch (e) { /* skip */ }
        }));
        items = items.map(w => ({ ...w, workerName: w.workerName || nameMap[w.workerId] || 'Unknown worker' }));
      }
      setPendingWork(items);
    } catch (e) { console.error('pendingWork', e); }
  }

  // Coaches owed for classes taught since their last payment. Mirrors the
  // calculation behind the People page's coach detail dialog so the two agree.
  async function loadUnpaidCoaches() {
    try {
      const today = localToday();
      const [coachSnap, paySnap, classSnap] = await Promise.all([
        getDocs(query(collection(db, 'users'), where('roles', 'array-contains-any', ['coach', 'assistant_coach']))),
        getDocs(collection(db, 'coachPayments')),
        // One bounded read rather than a query per coach — a coach's classes
        // can't be filtered by head OR assistant in a single Firestore query.
        getDocs(query(collection(db, 'classes'), where('date', '<=', today), orderBy('date', 'desc'), limit(800))),
      ]);
      const classes = classSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(c => c.status !== 'canceled');
      if (classSnap.size === 800) console.warn('loadUnpaidCoaches: hit the 800-class bound; older classes not considered');

      const lastPaid = {};
      paySnap.docs.forEach(d => {
        const p = d.data();
        const when = p.datePaid || (p.paidAt?.seconds ? dateStr(new Date(p.paidAt.seconds * 1000)) : '');
        if (p.coachId && when && (!lastPaid[p.coachId] || when > lastPaid[p.coachId])) lastPaid[p.coachId] = when;
      });

      const hoursOf = (list) => list.reduce((sum, c) => {
        if (!c.startTime || !c.endTime) return sum + 1;
        const [sh, sm] = c.startTime.split(':').map(Number);
        const [eh, em] = c.endTime.split(':').map(Number);
        return sum + ((eh * 60 + em) - (sh * 60 + sm)) / 60;
      }, 0);

      const rows = [];
      for (const d of coachSnap.docs) {
        const c = { id: d.id, ...d.data() };
        const since = lastPaid[c.id] || '';
        const mine = classes.filter(k =>
          (k.headCoachId === c.id || (k.assistantCoachIds || []).includes(c.id)) && (k.date || '') > since);
        if (!mine.length) continue;
        const headRate = parseFloat(c.headRatePerHour) || 0;
        const asstRate = parseFloat(c.assistantRatePerHour) || headRate;
        const asHead = mine.filter(k => k.headCoachId === c.id);
        const asAsst = mine.filter(k => k.headCoachId !== c.id);
        const amount = hoursOf(asHead) * headRate + hoursOf(asAsst) * asstRate;
        // Only coaches with an actual balance.
        //
        // Two different things reach zero here and only one of them is worth
        // saying out loud. A volunteer is paid nothing on purpose — Andy Lopez
        // and Kenneth Corbo both coach for free — and warning about them fired
        // on every dashboard load, with a class count that only ever grows.
        // A coach with no rate CONFIGURED is the case this warning is for: they
        // compute to the same zero and would be passed over silently as though
        // they were owed nothing.
        const isVolunteer = c.isVolunteer === true || (c.roles || []).includes('volunteer');
        const owed = Math.round(amount * 100) / 100;
        if (owed <= 0) {
          if (headRate === 0 && !isVolunteer) {
            console.warn(`unpaidCoaches: ${c.firstName || c.id} has ${mine.length} unpaid class(es) but no hourly rate set, so nothing is owed on paper`);
          }
          continue;
        }
        rows.push({
          id: c.id,
          name: `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || c.id,
          classes: mine.length,
          hours: Math.round(hoursOf(mine) * 10) / 10,
          amount: owed,
          since: since || 'never paid',
        });
      }
      rows.sort((a, b) => b.amount - a.amount || b.classes - a.classes);
      setUnpaidCoaches(rows);
    } catch (e) { console.error('unpaidCoaches', e); }
  }

  async function loadShirtTasks() {
    try {
      const snap = await getDocs(query(collection(db, 'shirtTasks'), where('done', '==', false)));
      const tasks = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setShirtTasks(tasks);
    } catch (e) {
      console.error('Failed to load shirt tasks:', e);
    }
  }

  async function markShirtDone(taskId) {
    try {
      await updateDoc(doc(db, 'shirtTasks', taskId), {
        done: true,
        doneAt: new Date(),
        doneBy: user.uid,
      });
      setShirtTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (e) {
      console.error('Mark shirt done failed:', e);
      alert('Failed to mark done: ' + e.message);
    }
  }

  async function acknowledgeClass(classId) {
    try {
      await updateDoc(doc(db, 'classes', classId), {
        noShowAcknowledged: true,
        noShowAcknowledgedAt: new Date(),
        noShowAcknowledgedBy: user.uid,
      });
      setStaleClasses(prev => prev.filter(c => c.id !== classId));
    } catch (e) {
      console.error('Acknowledge failed:', e);
      alert('Failed to acknowledge: ' + e.message);
    }
  }

  function fmtClassLine(c) {
    const fmtTime = (t) => {
      if (!t) return '';
      const [h, m] = t.split(':').map(Number);
      return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
    };
    const dateLabel = new Date(c.date + 'T12:00:00').toLocaleDateString('en-US',
      { weekday: 'short', month: 'short', day: 'numeric' });
    const typeLabel = c.sessionType === 'junior-practice' ? 'Junior Practice' : 'Adult Practice';
    return `${dateLabel} · ${fmtTime(c.startTime)}–${fmtTime(c.endTime)} · ${typeLabel}`;
  }

  // The activity feed is assembled as HTML strings and injected with
  // dangerouslySetInnerHTML, so every value interpolated into it has to be
  // escaped here. Names, schools and plan names all come from what somebody
  // typed into the registration form, and an unescaped one would run in the
  // admin's dashboard rather than merely look wrong.
  const esc = (v) => String(v == null ? '' : v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  // A name in the feed, linked to whoever it names. The real href keeps the
  // link honest — middle-click and "open in new tab" work, and it is the
  // fallback if the click handler ever fails — while data-nav is what the
  // panel's handler reads to route in-app instead of reloading the page.
  const personLink = (href, name) =>
    `<a class="tbv-activity-link" href="${esc(href)}" data-nav="${esc(href)}"><strong>${esc(name)}</strong></a>`;

  async function loadActivity() {
    try {
      const thirtyDaysAgo = Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
      const items = [];

      const today = localToday();
      // These five reads have no dependency on each other; awaiting them in
      // sequence cost five serial round-trips for no reason.
      const [athleteSnap, purchaseSnap, classSnap, coachSnap, userSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'athletes'),
          where('registrationDate', '>=', thirtyDaysAgo),
          orderBy('registrationDate', 'desc'),
          limit(20)
        )),
        getDocs(query(
          collection(db, 'purchases'),
          where('purchaseDate', '>=', thirtyDaysAgo),
          orderBy('purchaseDate', 'desc'),
          limit(50)
        )),
        getDocs(query(
          collection(db, 'classes'),
          where('date', '>=', today),
          orderBy('date', 'asc'),
          limit(50)
        )),
        getDocs(query(
          collection(db, 'users'),
          where('roles', 'array-contains-any', ['coach', 'assistant_coach'])
        )),
        getDocs(query(
          collection(db, 'users'),
          where('registrationDate', '>=', thirtyDaysAgo),
          orderBy('registrationDate', 'desc'),
          limit(20)
        )),
      ]);
      const purchases = purchaseSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const upcomingClasses = classSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(c => !['block-time', 'league'].includes((c.sessionType || '').toLowerCase()));
      const coaches = coachSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Membership lookup for adult athletes. This used to be one query per
      // athlete, awaited inside the loop — up to 20 serial round-trips. Resolve
      // every email up front instead, chunked for Firestore's 30-value `in`
      // limit, so it costs at most one extra query.
      const adultEmails = [...new Set(athleteSnap.docs
        .map(d => d.data())
        .filter(a => a.athleteType === 'adult')
        .map(a => (a.email || '').trim().toLowerCase())
        .filter(Boolean))];
      const membershipByEmail = new Map();
      if (adultEmails.length) {
        const chunks = [];
        for (let i = 0; i < adultEmails.length; i += 30) chunks.push(adultEmails.slice(i, i + 30));
        const snaps = await Promise.all(chunks.map(c =>
          getDocs(query(collection(db, 'users'), where('email', 'in', c)))
        ));
        snaps.forEach(sn => sn.docs.forEach(d => {
          const u = d.data();
          const em = (u.email || '').trim().toLowerCase();
          if (em && !membershipByEmail.has(em)) membershipByEmail.set(em, u.membership);
        }));
      }

      for (const doc of athleteSnap.docs) {
        const a = { id: doc.id, ...doc.data() };
        const name = `${a.firstName} ${a.lastName}`;
        const pro = pronoun(a.gender, 'subject');
        const proPos = pronoun(a.gender, 'possessive');

        // Athlete rows point at the athlete profile, not the parent's user
        // profile — the feed line is about the athlete.
        const nameHtml = personLink(`/athletes/${a.id}`, name);
        let desc = `Welcome ${nameHtml}`;
        // Juniors get their program phrase from the junior branch below —
        // this one is for adults only (prevents doubling).
        //
        // "our adult program" rather than the skill group. Beginner /
        // intermediate / advanced is how someone described their own
        // experience on a form; announcing it back to the whole club reads as
        // the club grading them, and it is not what they joined. Members are
        // unaffected: their line is replaced further down with the membership
        // welcome, which says something they actually chose.
        if (a.athleteType !== 'junior') {
          desc += ' to our adult program.';
        }

        if (a.athleteType === 'junior') {
          // "Welcome [name] to our junior program. She/he attends [school],
          // grade [grade], class of [year]." — coach/class sentence removed.
          desc += ' to our junior program.';
          if (a.schoolName) {
            const gradYear = a.graduationYear || calcGradYear(a.currentGrade);
            desc += ` ${pro} attends ${esc(a.schoolName)}`;
            if (a.currentGrade) desc += `, grade ${a.currentGrade}`;
            if (gradYear) desc += `, class of ${gradYear}`;
            desc += '.';
          }
        }

        const purchase = purchases.find(p =>
          p.athleteId === a.athleteId || p.athleteId === a.id
        );

        if (a.athleteType === 'adult') {
          // Members get a membership welcome instead of a training-program one —
          // not every adult account trains. Non-member adults only get the
          // class line if they actually bought sessions.
          let memberTierShort = null;
          try {
            const em = (a.email || '').trim().toLowerCase();
            if (em) {
              const mem = membershipByEmail.get(em);
              if (mem?.tierId && ['active', 'trialing', 'frozen'].includes(mem.status)) {
                memberTierShort = (mem.tierName || 'Torch')
                  .split(' - ')[0]
                  .replace(/\s*membership\s*/i, ' ')
                  .trim() || 'Torch';
              }
            }
          } catch (e) { /* feed stays non-member on lookup failure */ }

          if (memberTierShort) {
            desc = `Welcome ${nameHtml} who just became a <strong>${esc(memberTierShort)} member</strong>.`;
          } else if (purchase) {
            const matchingClass = upcomingClasses.find(c =>
              (c.sessionType || '').toLowerCase().includes('adult')
            );
            if (matchingClass) {
              const coach = coaches.find(co => co.id === matchingClass.headCoachId);
              desc += ` ${pro} will be attending our adult class`;
              if (coach) desc += ` with Coach ${personLink(`/people/${coach.id}`, coach.firstName)}`;
              desc += '.';
            }
          }
        }
        if (purchase) {
          const added = Number(purchase.sessionsAdded);
          desc += ` ${pro} just added <strong>${esc(purchase.sessionsAdded)} session${added === 1 ? '' : 's'}</strong> (${esc(purchase.planName)}) to ${proPos} account.`;
        }

        items.push({ text: desc, date: a.registrationDate });
      }

      // New staff (fetched above alongside the other reads)
      for (const doc of userSnap.docs) {
        const u = { id: doc.id, ...doc.data() };
        const roles = u.roles || [];
        const staffRoles = roles.filter(r =>
          ['coach', 'admin', 'volunteer', 'assistant_coach'].includes(r)
        );
        if (staffRoles.length === 0) continue;

        const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
        const roleLabel = staffRoles.map(r => r.replace('_', ' ')).join(', ');
        items.push({
          text: `${personLink(`/people/${u.id}`, name)} joined as ${esc(roleLabel)}.`,
          date: u.registrationDate,
        });
      }

      items.sort((a, b) => {
        const da = a.date?.toDate ? a.date.toDate() : new Date(0);
        const db2 = b.date?.toDate ? b.date.toDate() : new Date(0);
        return db2 - da;
      });

      // The ten newest, across every kind of item — sorted above, so this is
      // the latest ten, not the first ten of whichever query ran first.
      setActivity(items.slice(0, 10));
    } catch (e) {
      console.error('Activity load error:', e);
    } finally {
      setLoadingActivity(false);
    }
  }

  async function loadEvents() {
    try {
      const today = localToday();
      const items = [];

      // Classes
      try {
        const classSnap = await getDocs(query(
          collection(db, 'classes'),
          where('date', '>=', today),
          orderBy('date', 'asc'),
          limit(10)
        ));
        for (const doc of classSnap.docs) {
          const c = { id: doc.id, ...doc.data() };
          if (['block-time', 'league'].includes((c.sessionType || '').toLowerCase())) continue;
          items.push({
            id: c.id,
            name: c.eventName || c.sessionType || c.title || 'Class',
            meta: `${fmtDateStr(c.date)}${c.startTime ? ' · ' + fmtTime(c.startTime) : ''}${c.skillGroup ? ' · ' + c.skillGroup : ''}`,
            badge: formatSessionLabel(c.sessionType),
            cancelled: c.status === 'canceled',
            sortDate: c.date,
          });
        }
      } catch (e) { console.warn('Classes query failed:', e.message); }

      // Tournaments
      try {
        const tournSnap = await getDocs(query(
          collection(db, 'tournaments'),
          where('date', '>=', today),
          orderBy('date', 'asc'),
          limit(10)
        ));
        for (const doc of tournSnap.docs) {
          const t = { id: doc.id, ...doc.data() };
          // Cancellation can be recorded several ways (status, flags, or the
          // tournament_status field the admin cancel/archive flow sets).
          const status = String(t.status || '').toLowerCase();
          const tStatus = String(t.tournament_status || '').toLowerCase();
          const isCancelled =
            status === 'canceled' || status === 'cancelled' ||
            t.canceled === true || t.cancelled === true ||
            tStatus.startsWith('canceled') || tStatus.startsWith('cancelled');
          // Archived (active:false) but NOT cancelled = removed; hide it.
          // Cancelled stays visible, marked, so members see it's off.
          if (t.active === false && !isCancelled) continue;
          // Only show published tournaments (publish_date in the past or not set)
          if (t.publish_date && t.publish_date > today) continue;
          items.push({
            id: t.id,
            name: t.name,
            meta: `${fmtDateStr(t.date)}${t.location ? ' · ' + t.location : ''}`,
            badge: ({ tournament: 'Tournament', clinic: 'Clinic', camp: 'Camp', tryout: 'Tryout', event: 'Event' })[t.eventType] || 'Tournament',
            cancelled: isCancelled,
            special: true,
            sortDate: t.date,
          });
        }
      } catch (e) { console.warn('Tournaments query failed:', e.message); }

      // Leagues. A league is a season, not a date: it runs for weeks, so the
      // three statuses the Leagues page treats as live are what belongs here
      // rather than a date window. One already under way still sorts to today,
      // or a season that started in September would rank above tomorrow's
      // class and then get cut by the slice below.
      try {
        const leagueSnap = await getDocs(collection(db, 'leagues'));
        for (const doc of leagueSnap.docs) {
          const lg = { id: doc.id, ...doc.data() };
          if (!['registration', 'active', 'playoffs'].includes(lg.status)) continue;
          const started = lg.startDate && lg.startDate <= today;
          items.push({
            id: lg.id,
            name: lg.name || 'League',
            meta: started
              ? `${lg.status === 'playoffs' ? 'Playoffs' : 'In progress'}${lg.startDate ? ' · started ' + fmtDateStr(lg.startDate) : ''}`
              : `${lg.startDate ? 'Starts ' + fmtDateStr(lg.startDate) : 'Dates TBA'}${lg.status === 'registration' ? ' · registration open' : ''}`,
            badge: 'League',
            special: true,
            sortDate: started ? today : (lg.startDate || today),
          });
        }
      } catch (e) { console.warn('Leagues query failed:', e.message); }

      // Birthdays this week
      try {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const weekEnd = new Date(now);
        weekEnd.setDate(now.getDate() + 7);
        const currentYear = now.getFullYear();
        const athleteSnap = await getDocs(collection(db, 'athletes'));
        const ninetyDaysAgo = dateStr(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000));
        for (const d of athleteSnap.docs) {
          const a = d.data();
          if (!a.birthday || !a.firstName) continue;
          // Skip archived athletes
          if (a.archived) continue;
          // Skip if no session balance defined AND no recent check-in (truly inactive)
          const hasSessionBalance = a.sessionBalance !== undefined && a.sessionBalance !== null;
          const recentCheckin = a.lastCheckIn && (
            a.lastCheckIn?.toDate ? dateStr(a.lastCheckIn.toDate()) : String(a.lastCheckIn)
          ) >= ninetyDaysAgo;
          if (!hasSessionBalance && !recentCheckin) continue;
          const [, bMonth, bDay] = a.birthday.split('-');
          const bdayThisYear = new Date(currentYear, parseInt(bMonth) - 1, parseInt(bDay));
          if (bdayThisYear >= now && bdayThisYear <= weekEnd) {
            const age = currentYear - parseInt(a.birthday.split('-')[0]);
            items.push({
              id: d.id,
              name: `🎂 ${a.firstName} ${a.lastName}`,
              meta: `${fmtDateStr(`${currentYear}-${bMonth}-${bDay}`)} · Turning ${age}`,
              badge: 'Birthday',
              sortDate: `${currentYear}-${bMonth}-${bDay}`,
            });
          }
        }
      } catch (e) { console.warn('Birthdays query failed:', e.message); }

      items.sort((a, b) => (a.sortDate > b.sortDate ? 1 : -1));
      const seen = new Set();
      const deduped = items.filter(ev => {
        const key = ev.id || `${ev.name}|${ev.sortDate}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      // Reserve the cap for one-off events, or a run of classes fills every
      // slot and the tournament or league worth knowing about drops off the
      // end. Date order survives; only what gets cut changes.
      const LIMIT = 12;
      const specials = deduped.filter(i => i.special).slice(0, LIMIT);
      const routine = deduped.filter(i => !i.special).slice(0, Math.max(0, LIMIT - specials.length));
      const keep = new Set([...specials, ...routine]);
      setEvents(deduped.filter(i => keep.has(i)));
    } catch (e) {
      console.error('Events load error:', e);
    } finally {
      setLoadingEvents(false);
    }
  }

  return (
    <Layout>
      <style>{styles}</style>
      <div className="tbv-dash">

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <div className="tbv-dash-welcome">Welcome back, {firstName}</div>
            <div className="tbv-dash-sub">Torch Beach Volleyball — What's New</div>
          </Box>
          {isStaffViewer && (staleClasses.length > 0 || shirtTasks.length > 0 || pendingWork.length > 0 || unpaidCoaches.length > 0) && (
            <Box>
              <IconButton onClick={(e) => setBellAnchor(e.currentTarget)}
                          sx={{ color: '#C9A84C' }}
                          title={`${staleClasses.length} past practice${staleClasses.length === 1 ? '' : 's'} with no check-ins`}>
                {/* One per topic, not one per item. Summing the items put 15 on
                    the bell when there were four things to look at, which reads
                    as a backlog rather than a to-do list. The menu below still
                    shows every item. */}
                <Badge badgeContent={[staleClasses, shirtTasks, pendingWork, unpaidCoaches]
                  .filter(group => group.length > 0).length} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              <Menu anchorEl={bellAnchor} open={Boolean(bellAnchor)} onClose={() => setBellAnchor(null)}
                    PaperProps={{ sx: { minWidth: 360, maxWidth: 480, maxHeight: 500 } }}>
                {staleClasses.length > 0 && (
                <Box onClick={() => staleClasses.length > 1 && setBellOpen(o => ({ ...o, stale: !o.stale }))}
                  sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee', cursor: staleClasses.length > 1 ? 'pointer' : 'default',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F' }}>
                      Past practice with no check-ins
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      {staleClasses.length} class{staleClasses.length === 1 ? '' : 'es'} need attention
                    </Typography>
                  </Box>
                  {staleClasses.length > 1 && <Typography sx={{ color: '#888' }}>{bellOpen.stale ? '▾' : '▸'}</Typography>}
                </Box>
                )}
                {(staleClasses.length === 1 || bellOpen.stale) && staleClasses.map((c) => (
                  <Box key={c.id} sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E3A5F', mb: 0.5 }}>
                      {fmtClassLine(c)}
                    </Typography>
                    {c.skillGroup && (
                      <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                        {c.skillGroup}{c.courtNumber ? ` · Court ${String(c.courtNumber).replace('court-','')}` : ''}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                      <Button size="small" variant="outlined" color="error"
                              onClick={() => { setCancelDialog(c); setBellAnchor(null); }}>
                        Cancel
                      </Button>
                      <Button size="small" variant="outlined"
                              onClick={() => acknowledgeClass(c.id)}>
                        Acknowledge
                      </Button>
                      <Button size="small" variant="text"
                              onClick={() => { setBellAnchor(null); navigate(`/schedule?classId=${c.id}`); }}>
                        View
                      </Button>
                    </Box>
                  </Box>
                ))}
                {shirtTasks.length > 0 && (
                  <Box onClick={() => shirtTasks.length > 1 && setBellOpen(o => ({ ...o, shirts: !o.shirts }))}
                    sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee', bgcolor: '#FBF8F0', cursor: shirtTasks.length > 1 ? 'pointer' : 'default',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F' }}>
                        Shirts to give out
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {shirtTasks.length} new junior{shirtTasks.length === 1 ? '' : 's'}
                      </Typography>
                    </Box>
                    {shirtTasks.length > 1 && <Typography sx={{ color: '#888' }}>{bellOpen.shirts ? '▾' : '▸'}</Typography>}
                  </Box>
                )}
                {(shirtTasks.length === 1 || bellOpen.shirts) && shirtTasks.map((t) => (
                  <Box key={t.id} sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E3A5F', mb: 0.25 }}>
                      {t.athleteName || 'Junior athlete'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                      Shirt size: {t.shirtSize || '—'}{t.planName ? ` · ${t.planName}` : ''}
                    </Typography>
                    <Button size="small" variant="contained"
                            sx={{ backgroundColor: '#C9A84C', '&:hover': { backgroundColor: '#B8963C' } }}
                            onClick={() => markShirtDone(t.id)}>
                      Mark shirt given
                    </Button>
                  </Box>
                ))}
                {pendingWork.length > 0 && (
                  <Box onClick={() => pendingWork.length > 1 && setBellOpen(o => ({ ...o, work: !o.work }))}
                    sx={{ px: 2, py: 1.5, borderTop: '1px solid #eee', cursor: pendingWork.length > 1 ? 'pointer' : 'default',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F' }}>
                        Unpaid contractor work
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {pendingWork.length} entr{pendingWork.length === 1 ? 'y' : 'ies'} · ${pendingWork.reduce((t, w) => t + (w.amount || 0), 0).toFixed(2)} owed
                      </Typography>
                    </Box>
                    {pendingWork.length > 1 && <Typography sx={{ color: '#888' }}>{bellOpen.work ? '▾' : '▸'}</Typography>}
                  </Box>
                )}
                {(pendingWork.length === 1 || bellOpen.work) && pendingWork.map((w) => (
                  <MenuItem key={w.id} onClick={() => { setBellAnchor(null); navigate('/people'); }}
                    sx={{ whiteSpace: 'normal', alignItems: 'flex-start', py: 1 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {w.workerName || 'Worker'} — {w.amount != null ? `$${Number(w.amount).toFixed(2)}` : 'unpriced'}
                        {w.amount == null ? ' · needs pricing' : w.approved === false ? ' · needs approval' : ' · unpaid'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {w.date}{w.hours ? ` · ${w.hours}h` : ''}{w.description ? ` · ${w.description.slice(0, 50)}` : ''}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
                {unpaidCoaches.length > 0 && (
                  <Box onClick={() => unpaidCoaches.length > 1 && setBellOpen(o => ({ ...o, coaches: !o.coaches }))}
                    sx={{ px: 2, py: 1.5, borderTop: '1px solid #eee', bgcolor: '#FBF8F0',
                      cursor: unpaidCoaches.length > 1 ? 'pointer' : 'default',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F' }}>
                        Unpaid coaches
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {unpaidCoaches.length} coach{unpaidCoaches.length === 1 ? '' : 'es'} ·{' '}
                        ${unpaidCoaches.reduce((t, c) => t + c.amount, 0).toFixed(2)} owed
                      </Typography>
                    </Box>
                    {unpaidCoaches.length > 1 && <Typography sx={{ color: '#888' }}>{bellOpen.coaches ? '▾' : '▸'}</Typography>}
                  </Box>
                )}
                {(unpaidCoaches.length === 1 || bellOpen.coaches) && unpaidCoaches.map((c) => (
                  <MenuItem key={c.id} onClick={() => { setBellAnchor(null); navigate('/people?tab=Coaches'); }}
                    sx={{ whiteSpace: 'normal', alignItems: 'flex-start', py: 1 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {c.name} — ${c.amount.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {c.classes} class{c.classes === 1 ? '' : 'es'} · {c.hours}h · since {c.since}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          )}
        </Box>
        <div className="tbv-dash-divider" />

        {/* New adult: "What brings you to Torch?" until answered or put off.
            After sending it stays up to show the confirmation (and the member
            plans button); a reload retires it. */}
        {showAdultSurvey && (
          <Box sx={{ mb: 3 }}>
            <AdultNextSteps onDismiss={() => {
              setShowAdultSurvey(false);
              httpsCallable(getFunctions(), 'dismissAdultInterests')().catch(() => {});
            }} />
          </Box>
        )}

        {/* Pending tournament payments */}
        {partialRegs.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            {partialRegs.map(reg => (
              <div key={reg.id} style={{
                background: 'rgba(255,193,7,0.1)', border: '1px solid rgba(255,193,7,0.3)',
                borderRadius: 10, padding: '14px 20px', marginBottom: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
              }}>
                <div>
                  <div style={{ color: '#FFC107', fontWeight: 700, fontSize: '0.88rem', marginBottom: 2 }}>
                    ⚠ Pending Balance — {reg.tournament_name}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem' }}>
                    {reg.division} Division · ${(reg.amount_paid / 100).toFixed(2)} paid · balance due before tournament
                  </div>
                </div>
                <a href={`/tournament-pay?reg_id=${reg.id}`}
                  style={{
                    background: '#FFC107', color: '#0D1E35', fontWeight: 700,
                    padding: '8px 18px', borderRadius: 7, textDecoration: 'none',
                    fontSize: '0.82rem', letterSpacing: '0.5px', whiteSpace: 'nowrap',
                  }}>
                  Complete Payment →
                </a>
              </div>
            ))}
          </div>
        )}

        {/* Newsletter prompt — shown until the account signs up or skips it */}
        {nlForm && (
          <div className="tbv-news-panel">
            <div className="tbv-news-copy">
              <div className="tbv-news-title">Club Newsletter</div>
              <div className="tbv-news-sub">
                Tournaments, leagues, camps and open play. Every email has an unsubscribe link.
              </div>
            </div>
            {nlMsg?.ok ? (
              <div className="tbv-news-msg tbv-news-ok" style={{ flex: '2 1 380px' }}>{nlMsg.text}</div>
            ) : (
              <form className="tbv-news-form" onSubmit={handleSubscribe}>
                <input className="tbv-news-input" type="text" placeholder="Your name"
                       value={nlForm.name} onChange={setNl('name')} autoComplete="name" />
                <input className="tbv-news-input" type="email" placeholder="Email address"
                       value={nlForm.email} onChange={setNl('email')} autoComplete="email" />
                {/* Honeypot — hidden from humans, bots fill it and get silently dropped */}
                <input type="text" name="website" value={nlForm.website} onChange={setNl('website')}
                       style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
                       tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <button className="tbv-news-btn" type="submit" disabled={nlSending}>
                  {nlSending ? 'Signing up…' : 'Sign Up'}
                </button>
                <button className="tbv-news-skip" type="button" onClick={dismissNewsletterPrompt}>
                  No thanks
                </button>
                {nlMsg && !nlMsg.ok && <div className="tbv-news-msg tbv-news-err">{nlMsg.text}</div>}
              </form>
            )}
          </div>
        )}

        {/* Recent Activity panel — defined once, rendered as tab on mobile or column on desktop */}
        {(() => {
          const activityPanel = (
            <div className="tbv-panel">
              {!isMobile && <div className="tbv-panel-title">Recent Activity</div>}
              {loadingActivity ? (
                <div className="tbv-spinner">
                  <CircularProgress size={28} sx={{ color: '#C9A84C' }} />
                </div>
              ) : activity.length === 0 ? (
                <div className="tbv-empty">No new athletes or staff in the last 30 days.</div>
              ) : (
                activity.map((item, i) => (
                  <div key={i} className="tbv-activity-item">
                    {/* The names inside are real anchors, so ctrl-click and
                        "open in new tab" behave. This intercepts the plain
                        click only, to route in-app rather than reload the
                        whole dashboard for a profile. */}
                    <div
                      onClick={(e) => {
                        const link = e.target.closest('a[data-nav]');
                        if (!link || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                        e.preventDefault();
                        navigate(link.getAttribute('data-nav'));
                      }}
                      // Profiles and athlete pages open only for staff and the
                      // people they belong to (utils/profileAccess), so for
                      // everyone else a link to one is just the name.
                      dangerouslySetInnerHTML={{ __html: isStaffViewer ? item.text
                        : item.text.replace(/<a class="tbv-activity-link" href="\/(?:people|athletes)\/[^"]*"[^>]*>([\s\S]*?)<\/a>/g, '$1') }}
                    />
                    <div className="tbv-activity-date">{fmtDate(item.date)}</div>
                  </div>
                ))
              )}
            </div>
          );

          const eventsPanel = (
            <div className="tbv-panel">
              {!isMobile && <div className="tbv-panel-title">Upcoming Events</div>}
              {loadingEvents ? (
                <div className="tbv-spinner">
                  <CircularProgress size={28} sx={{ color: '#C9A84C' }} />
                </div>
              ) : events.length === 0 ? (
                <div className="tbv-empty">Nothing scheduled right now.</div>
              ) : (
                events.map((ev, i) => (
                  <div key={i} className={['Tournament','Clinic','Camp','Event','Tryout','League'].includes(ev.badge) ? 'tbv-event-item tbv-event-item-tournament' : 'tbv-event-item'}>
                    <div className="tbv-event-name">
                      <span className={
                        ev.badge === 'Birthday' ? 'tbv-event-badge-birthday'
                        : ev.badge === 'Tournament' ? 'tbv-event-badge-tournament'
                        : ev.badge === 'Clinic' ? 'tbv-event-badge-clinic'
                        : ev.badge === 'Camp' ? 'tbv-event-badge-camp'
                        : ev.badge === 'League' ? 'tbv-event-badge-league'
                        : ev.badge === 'Tryout' ? 'tbv-event-badge-tournament'
                        : ev.badge === 'Event' ? 'tbv-event-badge-tournament'
                        : 'tbv-event-badge'
                      }>{ev.badge}</span>
                      <span style={ev.cancelled ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>{ev.name}</span>
                      {ev.cancelled && (
                        <span style={{ fontSize: '0.56rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', padding: '2px 6px', borderRadius: '4px', background: '#C0392B', color: 'white' }}>Canceled</span>
                      )}
                    </div>
                    <div className="tbv-event-meta">{ev.meta}</div>
                  </div>
                ))
              )}
            </div>
          );

          if (isMobile) {
            return (
              <Box>
                <Tabs
                  value={mobileTab}
                  onChange={(_, v) => setMobileTab(v)}
                  variant="fullWidth"
                  sx={{
                    mb: 2,
                    borderBottom: '1px solid rgba(201,168,76,0.15)',
                    '& .MuiTab-root': {
                      color: 'rgba(255,255,255,0.55)',
                      fontFamily: '"Bebas Neue", sans-serif',
                      letterSpacing: '2px',
                      fontSize: '0.85rem',
                    },
                    '& .Mui-selected': { color: '#C9A84C !important' },
                    '& .MuiTabs-indicator': { backgroundColor: '#C9A84C' },
                  }}
                >
                  <Tab label="Recent Activity" />
                  <Tab label="Upcoming Events" />
                </Tabs>
                {mobileTab === 0 ? activityPanel : eventsPanel}
              </Box>
            );
          }

          return (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>{activityPanel}</Grid>
              <Grid size={{ xs: 12, md: 6 }}>{eventsPanel}</Grid>
            </Grid>
          );
        })()}

        {/* Active members — under both panels, on every screen size. Staff can
            open a profile; for everyone else it is a list of names. */}
        <div className="tbv-panel" style={{ marginTop: 24 }}>
          <div className="tbv-panel-title">
            Active Members{activeMembers ? ` (${activeMembers.length})` : ''}
          </div>
          {activeMembers === null ? (
            <div className="tbv-spinner">
              <CircularProgress size={28} sx={{ color: '#C9A84C' }} />
            </div>
          ) : activeMembers.length === 0 ? (
            <div className="tbv-empty">No active members to show.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '10px 16px' }}>
              {activeMembers.map(m => (
                <div key={m.id} style={{ minWidth: 0 }}>
                  {isStaffViewer ? (
                    <a className="tbv-activity-link" href={`/people/${m.id}`}
                      onClick={e => { e.preventDefault(); navigate(`/people/${m.id}`); }}
                      style={{ color: m.gold ? '#C9A84C' : 'white', fontSize: '0.85rem', fontWeight: 600 }}>
                      {m.name}
                    </a>
                  ) : (
                    <div style={{ color: m.gold ? '#C9A84C' : 'white', fontSize: '0.85rem', fontWeight: 600 }}>{m.name}</div>
                  )}
                  {m.plan && <div className="tbv-event-meta">{m.plan}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cancel-reason dialog for stale class bell — mirrors Schedule.js single-cancel UI */}
        <Dialog open={!!cancelDialog} onClose={() => { setCancelDialog(null); setCancelReason(''); }}
                maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 700 }}>
            Cancel Class
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {cancelDialog && (
              <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
                {fmtClassLine(cancelDialog)}
              </Typography>
            )}
            <TextField
              fullWidth select label="Cancellation Reason"
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              sx={{ mb: 1 }}
            >
              {['No Coach Available', 'Weather', 'Holiday', 'Low Attendance'].map(r => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => { setCancelDialog(null); setCancelReason(''); }}>Close</Button>
            <Button
              variant="contained" color="error"
              onClick={() => markClassCanceled(cancelDialog.id, cancelReason)}
              disabled={cancelSaving || !cancelReason}
            >
              {cancelSaving ? <CircularProgress size={20} /> : 'Cancel Class'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Layout>
  );
}

export default Dashboard;
